// Phase-2 Slice-22 DDL classifier — turns the SQL text the user just executed
// into a structured `DdlClassification` the index invalidator can dispatch on.
//
// Reuses `sqlTextMasker.maskStringsAndComments` so a value like
// `'CREATE VIEW foo'` inside a SELECT cannot trigger a false-positive match.
// Quoted identifiers ([…], "…", `…`) are preserved by the masker so we can
// extract names like `CREATE VIEW [dbo].[my view]` — WHICH of the five
// constructs delimits an identifier (vs. opens a string) is engine-dependent,
// so `engine` is threaded into the masker (PostgreSQL `ARRAY['DROP VIEW
// decoy']`, `$$DROP VIEW decoy$$` and `E'it\'s'` must have their strings
// masked WHOLE, since `[` there is an array constructor, `$$…$$` is a literal
// and a backslash inside `E'…'` escapes; Oracle `q'[…]'` the same), as are the
// two things the SQL TEXT cannot disclose — whether a MySQL session runs with
// `ANSI_QUOTES` (which decides if `DROP VIEW "v"` names a view or quotes a
// string) and with `NO_BACKSLASH_ESCAPES` (which decides whether `'it\'s'` is
// one string or two).
//
// v1 covers: CREATE / ALTER / DROP + (OR REPLACE)? + VIEW / FUNCTION /
// PROCEDURE / TRIGGER / TABLE. ALTER TABLE statements that mutate constraints (ADD /
// DROP CONSTRAINT, ADD / DROP CHECK, ALTER COLUMN ... SET DEFAULT, etc.) are
// flagged via `constraintAffected: true` so the invalidator can rebuild the
// connection's check-constraint + default-constraint records. 3-part names
// (db.schema.name) are not extracted in v1 — the catalog dispatch keys off
// schema + name only.

import type { DatabaseEngineId } from '@/core/engines';
import {
  maskStringsAndComments,
  type MaskOptions,
} from '@/features/workspace/common/utils/sqlTextMasker';

import type { QualifiedName } from '../types';

export type DdlOperation = 'create' | 'alter' | 'drop';
export type DdlTargetKind = 'view' | 'function' | 'procedure' | 'trigger' | 'table';

export type DdlClassification = {
  readonly operation: DdlOperation;
  readonly targetKind: DdlTargetKind;
  /** `objectName.quoted` says whether the NAME was delimited. */
  readonly objectName: QualifiedName;
  /** Whether the SCHEMA part (when present) was delimited — the invalidator
   *  folds each component to catalog case independently. */
  readonly schemaQuoted: boolean;
  readonly isOrReplace: boolean;
  readonly constraintAffected: boolean;
};

const SCAN_LIMIT = 4096;

// Accepts `CREATE [OR REPLACE]` (PG/MySQL/Oracle) AND `CREATE [OR ALTER]`
// (MSSQL proprietary). The capture group's presence flips `isOrReplace`.
const HEADER_RE =
  /\b(CREATE|ALTER|DROP)\s+(OR\s+(?:REPLACE|ALTER)\s+)?(VIEW|FUNCTION|PROCEDURE|TRIGGER|TABLE)\b/i;

/** Classify a DDL statement for the navigational-features index invalidator.
 *  Returns `null` when the SQL doesn't match a recognized DDL shape or when
 *  the object name can't be extracted (defensive — leaves the index unchanged
 *  rather than mis-invalidating).
 *
 *  `engine` picks the masker's delimiter row; every registration of a
 *  connection shares one engine, so the invalidator classifies once per DDL.
 *  Omitting it falls back to the masker's engine-agnostic default. `options`
 *  carries the masking context the text cannot disclose — the two MySQL
 *  `sql_mode` settings (`mysqlAnsiQuotes`, `mysqlNoBackslashEscapes`), which
 *  the invalidator reads off the connection's IntelliSense snapshot. */
export function classifyDdlForIndex(
  sql: string,
  engine?: DatabaseEngineId,
  options?: Omit<MaskOptions, 'engine'>,
): DdlClassification | null {
  const head = sql.slice(0, SCAN_LIMIT);
  const masked = maskStringsAndComments(head, { ...options, engine });
  const m = HEADER_RE.exec(masked);
  if (!m) return null;

  const operation = m[1].toLowerCase() as DdlOperation;
  const isOrReplace = m[2] !== undefined;
  const targetKind = m[3].toLowerCase() as DdlTargetKind;

  const headerEnd = m.index + m[0].length;
  // Skip optional `IF [NOT] EXISTS` after the kind keyword.
  let cursor = skipWhitespace(masked, headerEnd);
  cursor = skipKeyword(masked, cursor, ['IF', 'NOT', 'EXISTS']);
  cursor = skipKeyword(masked, cursor, ['IF', 'EXISTS']);
  cursor = skipWhitespace(masked, cursor);

  const qualified = readQualifiedName(masked, cursor);
  if (!qualified) return null;

  const constraintAffected =
    targetKind === 'table' && operation === 'alter'
      ? detectAlterTableConstraintOp(masked.slice(headerEnd))
      : false;

  return {
    operation,
    targetKind,
    objectName: qualified.name,
    schemaQuoted: qualified.schemaQuoted,
    isOrReplace,
    constraintAffected,
  };
}

function skipWhitespace(text: string, pos: number): number {
  let p = pos;
  while (p < text.length && /\s/.test(text[p])) p++;
  return p;
}

function skipKeyword(text: string, pos: number, words: readonly string[]): number {
  let p = skipWhitespace(text, pos);
  const start = p;
  for (const word of words) {
    p = skipWhitespace(text, p);
    const slice = text.slice(p, p + word.length);
    if (slice.toUpperCase() !== word) return start;
    if (p + word.length < text.length && /[A-Za-z0-9_$]/.test(text[p + word.length])) return start;
    p += word.length;
  }
  return p;
}

type IdentifierPart = {
  readonly value: string;
  readonly endPos: number;
  /** The part was delimited (`"…"`, `[…]`, backticks). */
  readonly quoted: boolean;
};

function readIdentifierPart(text: string, start: number): IdentifierPart | null {
  const p = skipWhitespace(text, start);
  if (p >= text.length) return null;
  const ch = text[p];
  if (ch === '[') return readDelimitedPart(text, p, ']');
  if (ch === '"' || ch === '`') return readDelimitedPart(text, p, ch);
  const m = /^([A-Za-z_][A-Za-z0-9_$]*)/.exec(text.slice(p));
  if (!m) return null;
  return { value: m[1], endPos: p + m[1].length, quoted: false };
}

/** Reads a delimited identifier part opening at `text[start]`, consuming a
 *  doubled closer as one literal closer character rather than ending the
 *  part — every engine escapes its closing delimiter this way: `"a""b"` →
 *  `a"b` (PG/Oracle/MSSQL/SQLite/MySQL ANSI_QUOTES), `[a]]b]` → `a]b`
 *  (MSSQL), `` `a``b` `` → `a`b` (MySQL). The returned value is unescaped
 *  since the invalidator re-verifies it against the catalog as a literal
 *  name. Returns `null` when the part is unterminated. */
function readDelimitedPart(text: string, start: number, closer: string): IdentifierPart | null {
  let value = '';
  let i = start + 1;
  for (;;) {
    const closeIdx = text.indexOf(closer, i);
    if (closeIdx < 0) return null;
    value += text.slice(i, closeIdx);
    if (text[closeIdx + 1] === closer) {
      // Doubled closer: one literal closer char, keep scanning.
      value += closer;
      i = closeIdx + 2;
      continue;
    }
    return { value, endPos: closeIdx + 1, quoted: true };
  }
}

type ReadQualifiedName = { readonly name: QualifiedName; readonly schemaQuoted: boolean };

function readQualifiedName(text: string, start: number): ReadQualifiedName | null {
  const part1 = readIdentifierPart(text, start);
  if (!part1) return null;
  let pos = part1.endPos;
  if (text[pos] === '.') {
    pos++;
    const part2 = readIdentifierPart(text, pos);
    if (part2) {
      // 3-part `db.schema.name` collapses to `(schema, name)` since the index
      // keys off the lower two components.
      let schemaPart = part1;
      let namePart = part2;
      if (text[part2.endPos] === '.') {
        const part3 = readIdentifierPart(text, part2.endPos + 1);
        if (part3) {
          schemaPart = part2;
          namePart = part3;
        }
      }
      return {
        name: makeQualifiedName(schemaPart.value, namePart.value, namePart.quoted),
        schemaQuoted: schemaPart.quoted,
      };
    }
  }
  return { name: makeQualifiedName(undefined, part1.value, part1.quoted), schemaQuoted: false };
}

/** `quoted` records whether the NAME was delimited (`"…"`, `[…]`, backticks):
 *  the invalidator asks the catalog for an unquoted name in the engine's own
 *  case (Oracle upper, PostgreSQL lower) and for a quoted one as spelled. */
function makeQualifiedName(schema: string | undefined, name: string, quoted: boolean): QualifiedName {
  return {
    schema: schema || undefined,
    name,
    raw: schema ? `${schema}.${name}` : name,
    quoted,
    foldedName: name.toLowerCase(),
    foldedSchema: schema ? schema.toLowerCase() : undefined,
  };
}

const CONSTRAINT_OP_RES: readonly RegExp[] = [
  /\bADD\s+(CONSTRAINT|CHECK|UNIQUE|PRIMARY\s+KEY|FOREIGN\s+KEY|DEFAULT)\b/i,
  /\bDROP\s+(CONSTRAINT|CHECK|DEFAULT)\b/i,
  /\b(ALTER|MODIFY)\s+(COLUMN\s+)?[A-Za-z_$"`[][\w$"`\]]*\s+(SET\s+|DROP\s+)?DEFAULT\b/i,
  /\bADD\s+(COLUMN\s+)?[A-Za-z_$"`[][\w$"`\]]*\b[\s\S]*?\bDEFAULT\b/i,
];

function detectAlterTableConstraintOp(rest: string): boolean {
  return CONSTRAINT_OP_RES.some((r) => r.test(rest));
}
